import type { Finding, RecommendedActionCode, Severity } from "../types";
import { budgetFitScore } from "./scoring";

const SEVERITY_RANK: Readonly<Record<Severity, number>> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function priorityCompare(a: Finding, b: Finding): number {
  const severityDiff = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (severityDiff !== 0) {
    return severityDiff;
  }
  const costDiff = b.cost - a.cost;
  if (costDiff !== 0) {
    return costDiff;
  }
  return a.id.localeCompare(b.id);
}

/**
 * Deterministic reference priority set: sorts findings by severity (descending),
 * then cost (descending), then id (ascending), and greedily keeps each finding
 * whose cost still fits the budget (skip-and-continue). Does not mutate input.
 */
export function getExpectedFindings(findings: readonly Finding[], clientBudget: number): Finding[] {
  const sorted = [...findings].sort(priorityCompare);
  const selected: Finding[] = [];
  let runningSum = 0;
  for (const finding of sorted) {
    if (runningSum + finding.cost <= clientBudget) {
      selected.push(finding);
      runningSum += finding.cost;
    }
  }
  return selected;
}

export interface ReportScoreInput {
  findings: readonly Finding[];
  clientBudget: number;
  selectedFindingIds: readonly string[];
  chosenRecommendations: Readonly<Record<string, RecommendedActionCode>>;
  hasReportTemplate?: boolean;
  hasPriorityPlanner?: boolean;
  /** Fraction of the budget the client forgives (Reporting Lv3: 0.2). */
  budgetTolerance?: number;
}

/**
 * Weighted Report Builder score (0-100): coverage 0.4, correctness 0.3,
 * priority 0.2, budgetFit 0.1, rounded. A report template grants +5, clamped
 * to 100.
 */
export function calculateReportScore(input: ReportScoreInput): number {
  const { findings, clientBudget, selectedFindingIds, chosenRecommendations } = input;
  const byId = new Map(findings.map((finding) => [finding.id, finding]));
  const expected = getExpectedFindings(findings, clientBudget);
  const expectedIds = new Set(expected.map((finding) => finding.id));
  const selectedFindings = selectedFindingIds
    .map((id) => byId.get(id))
    .filter((finding): finding is Finding => finding !== undefined);

  const coverage =
    expected.length === 0
      ? 100
      : (100 * selectedFindingIds.filter((id) => expectedIds.has(id)).length) / expected.length;

  const correctness =
    selectedFindingIds.length === 0
      ? 0
      : (100 *
          selectedFindingIds.filter((id) => {
            const finding = byId.get(id);
            return finding !== undefined && chosenRecommendations[id] === finding.recommendedActionCode;
          }).length) /
        selectedFindingIds.length;

  let priority = 100;
  if (input.hasPriorityPlanner !== true && selectedFindings.length > 1) {
    let ordered = 0;
    for (let i = 0; i < selectedFindings.length - 1; i += 1) {
      const a = selectedFindings[i];
      const b = selectedFindings[i + 1];
      if (a && b && priorityCompare(a, b) <= 0) {
        ordered += 1;
      }
    }
    priority = (100 * ordered) / (selectedFindings.length - 1);
  }

  const totalCost = selectedFindings.reduce((sum, finding) => sum + finding.cost, 0);
  const toleratedBudget = clientBudget * (1 + (input.budgetTolerance ?? 0));
  const budgetFit = budgetFitScore(totalCost, toleratedBudget);

  const total = Math.round(0.4 * coverage + 0.3 * correctness + 0.2 * priority + 0.1 * budgetFit);
  return input.hasReportTemplate === true ? Math.min(100, total + 5) : total;
}
